import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { AuthenticationService } from './authentication.service';
import { environment } from './../../environments/environment';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {

    // ─── Refresh lock: prevents multiple parallel refresh calls ──────────────
    private isRefreshing = false;
    private refreshTokenSubject = new BehaviorSubject<string | null>(null);

    constructor(private authenticationService: AuthenticationService) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const currentUser = this.authenticationService.currentUserValue;
        const isLoggedIn = currentUser && currentUser.token;
        const isApiUrl = request.url.startsWith(environment.apiUrl);

        if (isApiUrl) {
            request = request.clone({
                setHeaders: {
                    'App-Source': 'Portal'
                }
            });
        }

        if (isLoggedIn && isApiUrl) {
            request = this._attachToken(request, currentUser.token);
        }

        return next.handle(request).pipe(
            catchError(error => {
                // On 401, try a silent refresh — but not for auth endpoints themselves
                if (error instanceof HttpErrorResponse &&
                    error.status === 401 &&
                    !request.url.includes('/Account/Token') &&
                    !request.url.includes('/Account/RefreshToken')
                ) {
                    return this._handle401(request, next);
                }
                return throwError(() => error);
            })
        );
    }

    // ─── Attach Bearer token + profileId / roleId params ─────────────────────
    private _attachToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
        return request.clone({
            params: request.params
                .set('profileId', localStorage.getItem('currentProfileId') ?? '')
                .set('roleId', localStorage.getItem('currentUserRoleId') ?? ''),
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        });
    }

    // ─── Silent refresh on 401 ────────────────────────────────────────────────
    private _handle401(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {

        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            return this.authenticationService.refreshToken().pipe(
                switchMap(user => {
                    this.isRefreshing = false;
                    this.refreshTokenSubject.next(user.token);
                    return next.handle(this._attachToken(request, user.token));
                }),
                catchError(err => {
                    // Refresh failed → force logout
                    this.isRefreshing = false;
                    this.authenticationService.logout();
                    return throwError(() => err);
                })
            );
        }

        // Another request is already refreshing — queue until new token arrives
        return this.refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap(token => next.handle(this._attachToken(request, token))),
            catchError(err => throwError(() => err))
        );
    }
}